import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import PhotoModal from './PhotoModal';
import { useAuth } from '../contexts/AuthContext';
import { useAlbums } from '../contexts/AlbumContext';

const PhotoGrid = ({ photos, albumTitle, isPublic = false, album, onPhotoDeleted }) => {
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [imageErrors, setImageErrors] = useState({});
  const [loadedImages, setLoadedImages] = useState({});
  const [deletingPhotos, setDeletingPhotos] = useState({});
  const { user } = useAuth();
  const { deletePhotoFromAlbum } = useAlbums();

  if (!photos || photos.length === 0) {
    return null;
  }

  const handleImageLoad = (photoId) => {
    setLoadedImages(prev => ({ ...prev, [photoId]: true }));
  };

  const handleImageError = (photoId) => {
    setImageErrors(prev => ({ ...prev, [photoId]: true }));
  };

  const handleDeletePhoto = async (photoId, e) => {
    // Prevent event bubbling to parent elements
    e.preventDefault();
    e.stopPropagation();

    if (!window.confirm('Are you sure you want to delete this photo?')) {
      return;
    }

    setDeletingPhotos(prev => ({ ...prev, [photoId]: true }));

    try {
      const result = await deletePhotoFromAlbum(photoId);
      if (result.success) {
        // Call the callback to refresh the album
        if (onPhotoDeleted) {
          onPhotoDeleted();
        }
      } else {
        alert('Failed to delete photo: ' + result.error);
      }
    } catch (error) {
      console.error('Error deleting photo:', error);
      alert('Failed to delete photo');
    } finally {
      setDeletingPhotos(prev => ({ ...prev, [photoId]: false }));
    }
  };

  // Simple permission checking - only album owners can delete photos
  const canDeletePhoto = (photo) => {
    return user && album && album.ownerId === user.id;
  };

  return (
    <>
      <div className="columns-1 sm:columns-2 lg:columns-3 xl:columns-4 gap-4 space-y-4">
        {photos.map((photo, index) => (
          <motion.div
            key={photo.id}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3, delay: index * 0.02 }}
            className="break-inside-avoid mb-4 group cursor-pointer relative"
            onClick={() => setSelectedPhoto(photo)}
          >
            <div className="relative overflow-hidden rounded-lg bg-gray-100 shadow-sm hover:shadow-md transition-all duration-300">
              {!loadedImages[photo.id] && !imageErrors[photo.id] && (
                <div className="absolute inset-0 image-placeholder"></div>
              )}

              {imageErrors[photo.id] ? (
                <div className="aspect-square flex items-center justify-center bg-gray-100">
                  <div className="text-center">
                    <div className="w-8 h-8 bg-gray-300 rounded mx-auto mb-2"></div>
                    <p className="text-xs text-gray-500">Failed to load</p>
                  </div>
                </div>
              ) : (
                <img
                  src={photo.url}
                  alt={photo.name || `Photo ${index + 1}`}
                  className={`w-full h-auto object-cover transition-all duration-300 group-hover:scale-105 ${loadedImages[photo.id] ? 'opacity-100' : 'opacity-0'}`}
                  loading="lazy"
                  onLoad={() => handleImageLoad(photo.id)}
                  onError={() => handleImageError(photo.id)}
                />
              )}

              {/* Delete button - only for album owners */}
              {canDeletePhoto(photo) && (
                <button
                  onClick={(e) => handleDeletePhoto(photo.id, e)}
                  disabled={deletingPhotos[photo.id]}
                  className="absolute top-2 right-2 bg-red-600 hover:bg-red-700 text-white w-8 h-8 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed z-10"
                  title="Delete photo"
                >
                  {deletingPhotos[photo.id] ? (
                    <div className="w-3 h-3 border border-white border-t-transparent rounded-full animate-spin"></div>
                  ) : (
                    '×'
                  )}
                </button>
              )}

              <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-300 flex items-end p-3">
                <div className="text-white text-xs opacity-0 group-hover:opacity-100 transition-opacity duration-300 font-light">
                  {photo.uploaderName && (
                    <p>By {photo.uploaderName}</p>
                  )}
                </div>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      <AnimatePresence>
        {selectedPhoto && (
          <PhotoModal
            photo={selectedPhoto}
            photos={photos}
            onClose={() => setSelectedPhoto(null)}
            onNavigate={setSelectedPhoto}
          />
        )}
      </AnimatePresence>
    </>
  );
};

export default PhotoGrid;